import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from "../datepicker/CommonDatePicker";
import config from "../common/Config";
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const ProfitLossReport = () => {
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [salesRapOptions, setSalesRapOptions] = useState([]);
  const [selectedSalesRap, setSelectedSalesRap] = useState(null);
  const [salesRap, setSalesRap] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [amtReport, setAmtReport] = useState("");
  const [data, setData] = useState([]);
  const [footerTotals, setFooterTotals] = useState({
    totalNetAmount: 0,
    totalCost: 0,
    totalProfit: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    setPage(0);
  };

  const fetchCustomerNameOptions = async (inputValue) => {
    if (inputValue.length < 3) {
      setCustomerOptions([]);
      return;
    }

    const formData = new FormData();
    formData.append("term", inputValue);
    formData.append("limit", "10");

    const apiSalesRapUrl = `${config.API_URL}/suggestions`;
    try {
      const response = await fetch(apiSalesRapUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (Array.isArray(data.results)) {
        const formattedOptions = data.results.map((item) => ({
          label: item.text,
          value: item.id,
        }));
        setCustomerOptions(formattedOptions);
      }
    } catch (error) {
    }
  };

  const handleCustomerInputChange = (event, newInputValue) => {
    setCustomerInputValue(newInputValue);
    if (newInputValue.length >= 3) {
      fetchCustomerNameOptions(newInputValue);
    } else {
      setCustomerOptions([]);
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
  };

  const handleChangeSalesRap = (event, newValue) => {
    setSelectedSalesRap(newValue);
    setSalesRap(newValue ? String(newValue.value) : "");
    setPage(0);
  };

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${config.API_URL}/getsalesrep_for_profit_and_loss`;
    try {
      const response = await fetch(apiSalesRapUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.sales_rep.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      setSalesRapOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange && !selectedCustomer && !salesRap) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: String(page + 1),
        start: String(page * rowsPerPage),
        length: String(rowsPerPage),
        searchValue,
        reportrange,
        customer_id: selectedCustomer?.value || '',
        sales_rep_id: salesRap,
        route_id: '',
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/get_profit_loss_report`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        setReportDate(json.form_date + " To Date : " + json.to_date);
        setAmtReport(
          json.netamount +
          "  | Cost Total : " +
          json.cost_total +
          " | Profit Total : " +
          json.profit_total
        );
        setFooterTotals({
          totalNetAmount: json.netamount || 0,
          totalCost: json.cost_total || 0,
          totalProfit: json.profit_total || 0
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchValue, selectedCustomer, salesRap]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();
  }, [accessToken]);

  useEffect(() => {
    if (reportrange || selectedCustomer || salesRap) {
      fetchReport();
    }
  }, [fetchReport, page, rowsPerPage]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setPage(0);
      fetchReport();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const searchFormData = () => {
    const startDate = dates?.[0] ? format(dates[0], "dd/MM/yyyy") : "";
    const endDate = dates?.[1] ? format(dates[1], "dd/MM/yyyy") : "";
    const searchDate = startDate ? `${startDate} - ${endDate}` : "";
    setReportrange(searchDate);
    setPage(0);
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1, gap: 2 }}>
              <Typography className="page-title">Profit and Loss Report</Typography>
            </Box>

            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" sx={{ color: '#0e0f0fef' }} />
                      </InputAdornment>
                    ),
                  }}

                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                  style={{ width: '100%' }}
                  label="Date"
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  className="input-border"
                  shrink={true}
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  inputValue={customerInputValue}
                  onInputChange={handleCustomerInputChange}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField {...params}
                      label="Customer"
                      className="text-field input-border"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      placeholder="Search Customer" />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                <Autocomplete
                  className="input-border"
                  fullWidth
                  value={selectedSalesRap}
                  onChange={handleChangeSalesRap}
                  size="small"
                  options={salesRapOptions}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.value) === String(value?.value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} 
                    label="Sales Rep" 
                    placeholder="Select Sales Rep"
                    className="text-field input-border"
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 1 }} alignContent="end">
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={searchFormData}
                    className="button-variant-contained"

                    fullWidth
                  >
                    Submit
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>

            {(reportDate || amtReport) && (
              <Box sx={{ my:0.5, p:1, backgroundColor: '#e9ecf5', borderRadius: 1, display: 'flex', gap: 2}}>
                {reportDate && (
                  <Typography variant="body2" sx={{fontWeight: 600, fontSize: '13px', color: '#000' }}>
                    Profit and Loss From : <b style={{ fontWeight:600, fontSize: '12px' }}>{reportDate}</b>
                  </Typography>
                )}
                {amtReport && (
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: '#000' }}>
                    Net Amount :  <b style={{ fontWeight: 600, fontSize: '12px' }}>{amtReport}</b>
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <TableContainer>
            <Table size="medium"
              className="table-cell-border"
              sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="60px">Sr. No</TableCell>
                  <TableCell width="70px">Order Id</TableCell>
                  <TableCell width="80px">Order Date</TableCell>
                  <TableCell width="250px">Customer (A/C No)</TableCell>
                  <TableCell width="100px" align="right">Net Amount</TableCell>
                  <TableCell width="100px" align="right">Cost</TableCell>
                  <TableCell width="100px" align="right">Profit</TableCell>
                  <TableCell width="80px">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : data.length > 0 ? (
                  data.map((row, index) => (
                    <TableRow key={index}
                      hover
                      className="table-cell-style"
                    >
                      <TableCell>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{row.reference_no || ''}</TableCell>
                      <TableCell>{row.date || ''}</TableCell>
                      <TableCell>{row.customer || ''}</TableCell>
                      <TableCell align="right">{row.netamount || 0}</TableCell>
                      <TableCell align="right">{row.cost || 0}</TableCell>
                      <TableCell align="right">{row.profit || 0}</TableCell>
                      <TableCell>{row.status || ''}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#637381', fontSize: '0.75rem' }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow className="table-cell-style">
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 600 }}>
                    Total:
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {footerTotals.totalNetAmount || 0}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {footerTotals.totalCost || 0}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {footerTotals.totalProfit || 0}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              className="pagination-styles"
            />
        </Paper>
      </Box>

    </>
  );
};

export default ProfitLossReport;
